import _cloneDeep from "lodash-es/cloneDeep";
import _get from "lodash-es/get";

import { LL_FIELD_UNIQUE_IDENTIFIER, TRANSCRIPTION_FIELDS } from "../constants/transcription-fields";
import { Util } from "../util/util";

import { UserWithAccessToForm } from "./admin/UserWithAccessToForm";
import { Form, getIdByUniqueElementName } from "./form";
import {
    AddressElement,
    CheckboxVariants,
    ElementId,
    ElementOrSubElementId,
    FormElement,
    FormElementType,
    NameElement,
    SubElementId,
    SubElementMapping,
    getEmptyValueByElementType,
} from "./form-element";
import { SubmissionDataResponse, SubmissionResponse } from "./protocol/submission-response";
import { SubmissionQuestionData } from "./question-feeder";
import { VisitedStation } from "./station";
import { RatingValues, SubmissionActionData } from "./submission-action";
import { BusinessCardValue } from "../components/form-view/elements/business-card/business-card.component";
import type { AiCardResponse } from "./ai-cards";

export type OrderCheckboxSubmissionData = { values: { value: string; quantity: number }[]; note: string };
export type OrderRadioSubmissionData = { value: string; quantity: number; note: string };
export type AttachmentValue = { token: string; url: string; name: string; size: number; blob?: Blob };

/**
 * Value of a `simple_name` element: its visible sub-fields keyed by their transcription
 * identifier (`ll_field_unique_identifier`). The parent element has no value of its own — its
 * data lives under sub-element ids, which we resolve to identifiers via the element's `mapping`.
 * Hidden sub-fields are omitted, so keys are optional.
 */
export type NameSubmissionValue = {
    FirstName?: string;
    LastName?: string;
};

/**
 * Value of an `address` element: its visible sub-fields keyed by their transcription identifier
 * (`ll_field_unique_identifier`). See {@link NameSubmissionValue} for how ids are resolved.
 * Hidden sub-fields are omitted, so keys are optional.
 */
export type AddressSubmissionValue = {
    StreetAddress?: string;
    StreetAddress2?: string;
    City?: string;
    State?: string;
    Zipcode?: string;
    Country?: string;
};

/**
 * Maps each element type to the exact shape of its submitted value. Structural elements that never
 * hold a value (sections, separators, image placeholders, ...) are intentionally absent so they can
 * never appear in {@link VisibleSubmissionDataItem}.
 */
export type VisibleSubmissionElementValueMap = {
    [FormElementType.email]: string;
    [FormElementType.url]: string;
    [FormElementType.text]: string;
    [FormElementType.textarea]: string;
    [FormElementType.money]: string;
    [FormElementType.number]: string;
    [FormElementType.phone]: string;
    [FormElementType.simple_phone]: string;
    [FormElementType.date]: string;
    [FormElementType.time]: string;
    [FormElementType.datetime]: string;
    [FormElementType.audio]: string;
    [FormElementType.rating]: string;
    [FormElementType.star_rating]: string;
    [FormElementType.assign_owner]: string;
    [FormElementType.signature]: string;
    [FormElementType.barcode]: string;
    [FormElementType.meeting]: string;
    [FormElementType.activation]: string;
    [FormElementType.checkbox]: string[] | OrderCheckboxSubmissionData;
    [FormElementType.radio]: string | OrderRadioSubmissionData;
    [FormElementType.select]: string | OrderRadioSubmissionData;
    [FormElementType.image]: string[];
    [FormElementType.document]: number[];
    [FormElementType.engagem_feeder]: SubmissionQuestionData[];
    [FormElementType.business_card]: Partial<BusinessCardValue>;
    [FormElementType.boolean]: boolean;
    [FormElementType.attachments]: AttachmentValue[];
    [FormElementType.intelligence_crm]: AiCardResponse;
    [FormElementType.intelligence_ai]: AiCardResponse;
    [FormElementType.simple_name]: NameSubmissionValue;
    [FormElementType.address]: AddressSubmissionValue;
};

/** Element types that carry a user-facing value and therefore appear in visible submission data. */
export type VisibleSubmissionElementType = keyof VisibleSubmissionElementValueMap;

/**
 * A single entry in {@link FormSubmission.visible_submissions_data}. This is a discriminated union over
 * `element_type`, so narrowing on `element_type` gives a precisely-typed `element_value`.
 */
export type VisibleSubmissionDataItem = {
    [T in VisibleSubmissionElementType]: {
        element_id: ElementId;
        element_title: string;
        element_type: T;
        element_value: VisibleSubmissionElementValueMap[T];
    };
}[VisibleSubmissionElementType];

/** Structural / layout-only elements that never hold a submitted value. */
const STRUCTURAL_ELEMENT_TYPES: readonly FormElementType[] = [
    FormElementType.section,
    FormElementType.html_block,
    FormElementType.separator,
    FormElementType.speaker_section,
    FormElementType.session_section,
    FormElementType.image_placeholder,
];

type ValuedFormElement = Extract<FormElement, { type: VisibleSubmissionElementType }>;

function isValuedElement(element: FormElement): element is ValuedFormElement {
    return !STRUCTURAL_ELEMENT_TYPES.includes(element.type);
}

/**
 * Builds a composite element's value, keying each visible sub-field by its transcription
 * identifier (`ll_field_unique_identifier`) resolved from the element's `mapping`, rather than
 * by the opaque sub-element id — so hosts receive `{ FirstName, LastName }` /
 * `{ StreetAddress, City, ... }`. Sub-fields hidden by the element's configuration (e.g. an
 * address line turned off), or without a mapping entry, are skipped.
 */
function buildCompositeValue(
    element: NameElement | AddressElement,
    data: FormSubmission["data"],
): Partial<Record<LL_FIELD_UNIQUE_IDENTIFIER, string>> {
    const value: Partial<Record<LL_FIELD_UNIQUE_IDENTIFIER, string>> = {};
    // NameElement/AddressElement narrow `mapping` to SubElementMapping[] but keep the base
    // ParentElementMapping[] via the intersection, so assert the sub-element shape here.
    const mapping = (element.mapping ?? []) as SubElementMapping[];
    for (const subElement of element.sub_elements) {
        if (!subElement.visible) {
            continue;
        }
        const identifier = mapping.find(
            (m) => m.sub_element_id === subElement.sub_element_id,
        )?.ll_field_unique_identifier;
        if (!identifier) {
            continue;
        }
        value[identifier] = (data[subElement.sub_element_id] as string) ?? "";
    }
    return value;
}

function buildVisibleSubmissionItem(
    element: ValuedFormElement,
    data: FormSubmission["data"],
): VisibleSubmissionDataItem {
    if (element.type === FormElementType.simple_name) {
        return {
            element_id: element.id,
            element_title: element.title,
            element_type: element.type,
            element_value: buildCompositeValue(element, data) as NameSubmissionValue,
        };
    }
    if (element.type === FormElementType.address) {
        return {
            element_id: element.id,
            element_title: element.title,
            element_type: element.type,
            element_value: buildCompositeValue(element, data) as AddressSubmissionValue,
        };
    }

    const rawValue = data[element.id];
    const element_value = rawValue === undefined ? getEmptyValueByElementType(element) : rawValue;

    return {
        element_id: element.id,
        element_title: element.title,
        element_type: element.type,
        element_value,
    } as VisibleSubmissionDataItem;
}

/**
 * Builds the list of currently visible elements with their submitted values. Excludes:
 * - statically hidden elements (`is_hidden`),
 * - elements hidden by visibility rules / form type (passed in via `hiddenElementIds`),
 * - structural elements that never hold a value.
 */
export function getVisibleSubmissionsData(
    form: Form,
    data: FormSubmission["data"],
    hiddenElementIds: (ElementId | SubElementId)[],
): VisibleSubmissionDataItem[] {
    const hiddenSet = new Set<string>(hiddenElementIds);
    const items: VisibleSubmissionDataItem[] = [];
    for (const element of form.elements) {
        if (element.is_hidden || hiddenSet.has(element.id) || !isValuedElement(element)) continue;
        items.push(buildVisibleSubmissionItem(element, data));
    }
    return items;
}

export class FormSubmission {
    id: number = 0;
    form_id: number = 0;
    status: SubmissionStatus = SubmissionStatus.None;
    prospect_id: number = 0;
    email: string = "";
    company: string = "";
    jobTitle: string = "";
    phone: string = "";
    first_name: string = "";
    last_name: string = "";
    full_name: string = "";
    activity_id: number = 0;
    hold_request_id: number = 0;
    hold_submission: number = 0;
    hold_submission_reason: string = "";
    invalid_fields: 0 | 1 | "" = "";
    data: {
        [key: ElementOrSubElementId]:
            | string
            | string[]
            // documents
            | number[]
            // engagem
            | SubmissionQuestionData[]
            // business card
            | Partial<BusinessCardValue>
            //boolean element
            | boolean
            | OrderCheckboxSubmissionData
            | OrderRadioSubmissionData
            | AttachmentValue[]
            // intelligence crm / ai enrichment API response
            | AiCardResponse
            | null;
    } = {};
    submission_date: string = new Date().toISOString();
    last_sync_date: string = "";
    hidden_elements: string[] = [];
    visible_submissions_data: VisibleSubmissionDataItem[] = [];
    is_filled_from_list: 0 | 1 = 0;
    is_rapid_scan: 0 | 1 = 0;
    captured_by_user_name: string = "";
    captured_by_user_id: number = 0;
    submission_type: FormSubmissionType = FormSubmissionType.eventWebView;
    stations: VisitedStation[] = [];
    location: UserLocation = "";
    barcodeID: string = "";
    activation_id: number = 0;
    update_date: string = "";
    submit_message?: string = "";
    prospect_picture: string = "";
    submission_actions: SubmissionActionData = {};
    station_id: number | "" = "";
    station_touch: boolean = false;
    prospect: { id?: number; owner?: UserWithAccessToForm } = {};
    assign_owner_user: UserWithAccessToForm | null;
    prospect_tracking_info: {
        Email: string;
        goal_prospect_token: string;
        guid: string;
        ipAddress: string;
        prospect_id: number;
    } = {} as any;
    submission_token: string = "";
    query_parameters?: Record<string, string>;
    is_valid_for_goal: boolean = true;
    send_to_capture_portal: boolean = false;
    conversion_rate: number | null = null;
    submission_data_sales_price: number = 0;
    influenced_pipeline_value_correction: number = 0;
    he_invitation_code: string = "";

    public static getInstance(data: any): FormSubmission {
        const sub = new FormSubmission();
        if (!data) return sub;

        sub.id = parseInt(data.id) || data.id;
        sub.form_id = data.form_id;
        sub.status = data.status;
        sub.prospect_id = data.prospect_id || null;
        sub.email = data.email;
        sub.company = data.company;
        sub.phone = data.phone;
        sub.jobTitle = data.jobTitle;
        sub.first_name = data.first_name;
        sub.last_name = data.last_name;
        sub.full_name = data.full_name;
        sub.activation_id = data.activation_id || null;
        sub.activity_id = data.activity_id || null;
        sub.hold_request_id = data.hold_request_id || null;
        sub.hold_submission = data.hold_submission || null;
        sub.hold_submission_reason = data.hold_submission_reason;
        sub.invalid_fields = data.invalid_fields;
        sub.data = data.data;
        sub.submission_date = data.submission_date;
        sub.submission_type = data.submission_type;
        sub.last_sync_date = data.last_sync_date;
        sub.hidden_elements = data.hidden_elements;
        sub.visible_submissions_data = data.visible_submissions_data ?? [];
        sub.is_filled_from_list = data.is_filled_from_list;
        sub.is_rapid_scan = data.is_rapid_scan;
        sub.captured_by_user_id = data.captured_by_user_id;
        sub.captured_by_user_name = data.captured_by_user_name;
        sub.barcodeID = data.barcodeID;
        sub.stations = data.stations;
        sub.location = data.location;
        sub.activation_id = data.activation_id;
        sub.update_date = data.update_date;
        sub.submit_message = data.submit_message;
        sub.prospect_picture = data.prospect_picture;
        sub.submission_actions = data.submission_actions;
        sub.station_id = data.station_id;
        sub.station_touch = data.station_touch;
        sub.submission_actions.rating =
            sub.submission_actions.rating === null ? RatingValues.NONE : sub.submission_actions.rating;
        sub.prospect = data.prospect;
        sub.assign_owner_user = data.assign_owner_user;

        if (sub?.assign_owner_user) {
            sub.assign_owner_user.fullName = sub.assign_owner_user.firstname + " " + sub.assign_owner_user.lastname;
        }
        if (sub.prospect?.owner) {
            sub.prospect.owner.fullName = sub.prospect.owner.firstname + " " + sub.prospect.owner.lastname;
        }
        sub.prospect_tracking_info = data.prospect_tracking_info;
        sub.send_to_capture_portal = data.send_to_capture_portal;
        sub.conversion_rate = data.conversion_rate;
        sub.submission_data_sales_price = data.submission_data_sales_price;
        sub.influenced_pipeline_value_correction = data.influenced_pipeline_value_correction;
        sub.he_invitation_code = data.he_invitation_code;

        return sub;
    }

    public updateFields(form: Form): void {
        form.elements.forEach((element) => {
            switch (element.type) {
                case FormElementType.simple_name: {
                    this.first_name = (this.data[element.id + "_1"] as string) || "";
                    this.last_name = (this.data[element.id + "_2"] as string) || "";
                    this.full_name = Util.getFullName(this.first_name, this.last_name);
                    break;
                }
                case FormElementType.email:
                    this.email = (this.data[element.id] as string) || this.email || "";
                    break;
            }
        });
        let id = getIdByUniqueElementName("WorkPhone", form.elements);
        if (id) {
            this.phone = (this.data[id] as any) || "";
        }
        id = getIdByUniqueElementName("Company", form.elements);
        if (id) {
            this.company = (this.data[id] as any) || "";
        }
        id = getIdByUniqueElementName("JobTitle", form.elements);
        if (id) {
            this.jobTitle = (this.data[id] as any) || "";
        }
    }

    public getMappedFromResponse(item: SubmissionResponse, form: Form): void {
        this.id = item.device_submission_id || item.activity_id;
        this.data = reshapeSubmissionDataReturnedFromApi(item.data, form);
        this.form_id = form.form_id;
        this.updateFields(form);
    }

    public getElementsThatMapsToTranscriptionFields(form: Form): FormElement[] {
        return form.elements.filter((element) => {
            return element.mapping.some((e) => TRANSCRIPTION_FIELDS[e.ll_field_id]);
        });
    }

    public clone(): FormSubmission {
        return _cloneDeep(this);
    }
}

/**
 * The two shapes a submission's `data` can arrive in:
 * - `SubmissionDataResponse[]` — one entry per element, as the submissions API returns it for a
 *   *submitted* submission.
 * - `FormSubmission["data"]` — the flat element-id-keyed record the webview itself produces. It is
 *   what we emit as `submission_body`, and what the backend stores verbatim for a *draft*
 *   (`drafted-submissions` returns it back unchanged).
 *
 * Normalize with {@link normalizeSubmissionData} before filling a form with it.
 */
export type AnySubmissionData = SubmissionDataResponse[] | FormSubmission["data"];

/**
 * Reduce either submission data shape to the flat record the form fills from — reshaping the API's
 * array, or passing an already-flat record (a draft / a round-tripped `submission_body`) through.
 */
export function normalizeSubmissionData(data: AnySubmissionData | undefined, form: Form): FormSubmission["data"] {
    if (!data) return {};
    return Array.isArray(data) ? reshapeSubmissionDataReturnedFromApi(data, form) : data;
}

export function reshapeSubmissionDataReturnedFromApi(
    data: SubmissionDataResponse[],
    form: Form,
): FormSubmission["data"] {
    let reshapedData = {};
    data.forEach((dataItem) => {
        if (!dataItem.value) {
            return;
        }
        const elementId = dataItem.element_id;
        const element = form.getElementById(elementId);

        if (!element) return;

        switch (element.type) {
            case FormElementType.simple_name:
            case FormElementType.address:
                if (dataItem.value_splitted) {
                    const values = dataItem.value_splitted;
                    reshapedData = { ...reshapedData, ...values };
                }
                break;
            case FormElementType.document:
            case FormElementType.image:
                try {
                    reshapedData[elementId] = JSON.parse(dataItem.value);
                } catch (_) {
                    reshapedData[elementId] = [];
                }
                break;
            case FormElementType.business_card:
                try {
                    const data = JSON.parse(dataItem.value);
                    reshapedData[elementId] = {
                        front: _get(data, "front", ""),
                        back: _get(data, "back", ""),
                    };
                } catch (_) {
                    reshapedData[elementId] = {
                        front: "",
                        back: "",
                    };
                }
                break;
            case FormElementType.audio:
                reshapedData[elementId] = dataItem.value.replace(/\\/g, "");
                break;
            case FormElementType.checkbox:
                if (
                    element.variant === CheckboxVariants.orderCheckbox ||
                    element.variant === CheckboxVariants.orderRank ||
                    element.variant === CheckboxVariants.orderMultiselect
                ) {
                    reshapedData[elementId] = dataItem.value;
                } else {
                    reshapedData[elementId] = dataItem.value.split(";");
                }
                break;
            case FormElementType.boolean:
                reshapedData[elementId] = dataItem.value === "true";
                break;
            case FormElementType.attachments:
                try {
                    const parsed = typeof dataItem.value === "string" ? JSON.parse(dataItem.value) : dataItem.value;
                    reshapedData[elementId] = Array.isArray(parsed) ? parsed : [];
                } catch {
                    reshapedData[elementId] = [];
                }
                break;
            case FormElementType.intelligence_crm:
            case FormElementType.intelligence_ai:
                // The whole AiCardResponse is stored (sections + profileUrl + message), so a
                // legacy bare-sections array is treated as unusable and falls back to null.
                try {
                    const parsed = typeof dataItem.value === "string" ? JSON.parse(dataItem.value) : dataItem.value;
                    reshapedData[elementId] =
                        parsed !== null && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
                } catch {
                    reshapedData[elementId] = null;
                }
                break;
            default:
                reshapedData[elementId] = dataItem.value;
        }
    });
    return reshapedData;
}

export enum SubmissionStatus {
    None = 0, // initial status when creating submission
    Held = -1, // draft
}

// backend already uppercase them all, but they should always be lowercase in mobile
export enum FormSubmissionType {
    // normal = "normal",
    barcode = "webview_barcode",
    list = "webview_list",
    transcription = "transcription",
    activation = "activation",
    ocr = "ocr_transcription",
    eventWebView = "event_webview",
    webviewEnrichmentExistingDataSource = "webview_enrichment_existing_data_source",
    captelloExistDataSource = "captello_exist_data_source",
}

export type UserLocation =
    | {
          coords: {
              altitude: number;
              heading: number;
              latitude: number;
              accuracy: number;
              altitudeAccuracy: number;
              speed: number;
              longitude: number;
              timestamp: number;
          };
      }
    | "";
